import { FacilityId, PrefectureId } from '@gyym/sdk'

import { buildFacilityLink } from './buildFacilityLink'

type FacilityPathParams = {
  prefectureSlug?: string | string[]
  cityCode?: string | string[]
  facilityId?: string | string[]
}

/**
 * Resolve the path the facility page was requested with
 *
 * `pathname` is the `x-pathname` header set in `src/proxy.ts`, which holds the
 * path before rewrites. The proxy also runs on `_next/data` requests (Next
 * prepends the `_next/data` prefix to the matcher), so the header is normally
 * present. The path is rebuilt from the route params only as a fallback for
 * when the header is missing.
 *
 * @param pathname Value of the `x-pathname` header
 * @param params Route params of the facility page
 * @param suffix Path below the facility page, built from the raw route params
 * @returns string
 */
export const getRequestedFacilityPath = (
  pathname: string | string[] | null | undefined,
  params: FacilityPathParams,
  suffix = '',
): string =>
  typeof pathname === 'string'
    ? pathname
    : `/facilities/${params.prefectureSlug}/C${params.cityCode}/${params.facilityId}${suffix}`

/**
 * Check if the requested path is exactly the canonical path of the facility
 *
 * @param requestedPath Requested path (without query string)
 * @param facility Facility the page is going to show
 * @param suffix Canonical path below the facility page (e.g. `/reviews`)
 * @returns boolean
 */
export const isCanonicalFacilityPath = (
  requestedPath: string,
  facility: {
    id: FacilityId
    cityCode: number
    prefectureId: PrefectureId
  },
  suffix = '',
): boolean => requestedPath === `${buildFacilityLink(facility).as}${suffix}`
